import type { NextRequest, NextResponse } from 'next/server'

import { configPublic } from '@src/config.public'
import { getLocaleFromCode } from '@src/i18n/locale.util'
import type { CountryCode } from '@src/i18n/locales'
import { applyShopifyAccountLocale } from '@src/utils/externalShopifyLink'
import { log } from '@src/utils/log.util'

import { HEADERS } from './constants.util'

function getFallbackURL(): URL {
  return new URL(configPublic.general.siteMapUrl)
}

function getURLFromHeader(url: string | null): URL {
  const value = url?.trim()
  const headerState = value ? 'invalid' : url === null ? 'missing' : 'blank'

  if (value) {
    try {
      return new URL(value)
    } catch {
      // Fall through to the canonical origin below.
    }
  }

  log.warn('getCurrentURL received an unusable request URL header; using the canonical origin', {
    header: HEADERS.url,
    headerState,
  })
  return getFallbackURL()
}

export async function getCurrentServerURL(): Promise<URL> {
  const { headers } = await import('next/headers')
  return getURLFromHeader((await headers()).get(HEADERS.url))
}

/**
 * Get the current URL from the 'url' custom request header (server-side) or window.location (client-side).
 * Needed as NextRequest.nextUrl and NextRequest.url doesn't work with reverse proxy (CloudFront).
 **/
export async function getCurrentURL(req?: NextRequest | NextResponse) {
  // for middleware
  if (req) {
    return getURLFromHeader(req.headers.get(HEADERS.url))
  } // for server-side
  else if (typeof window === 'undefined') {
    return getCurrentServerURL()
  } // for client-side
  else {
    return new URL(window.location.href)
  }
}

/**
 * Same as getCurrentURL but overrides the hostname using the 'host' header in the HTTP request.
 * Needed because NextRequest.nextUrl and NextRequest.url doesn't translate '127.0.0.1' to 'localhost'.
 * @param req NextRequest or NextResponse for middleware usage. (Optional)
 * @returns URL
 */
export async function getCurrentURLFromHeaders(req?: NextRequest | NextResponse) {
  const currentUrl = await getCurrentURL(req)
  const currentHost = await getCurrentHost(req)
  return new URL(`${currentUrl.protocol}//${currentHost}${currentUrl.pathname}${currentUrl.search}`)
}

/**
 * Get the current host from the 'host' custom request header (server-side) or window.location.host (client-side).
 * Needed as NextRequest.nextUrl and NextRequest.url doesn't transtale '127.0.0.1' to 'localhost'.
 * @param req NextRequest or NextResponse for middleware usage. (Optional)
 * @returns string
 */
export async function getCurrentHost(req?: NextRequest | NextResponse) {
  const currentUrl = await getCurrentURL(req)
  // for middleware
  if (req) {
    return String(req.headers.get(HEADERS.host) ?? currentUrl.host)
  } // for server-side
  else if (typeof window === 'undefined') {
    const { headers } = await import('next/headers')
    return String((await headers()).get(HEADERS.host) ?? currentUrl.host)
  } // for client-side
  else {
    return window.location.host
  }
}

/** Content Detail Page paths; not end-anchored so CDP sub-paths also match. */
const CDP_PATH_REGEX = /^\/app\/[^/]+\/[^/]+\/(programs|content|blocks)\/[^/]+/

export function isCDPPath(pathname: string): boolean {
  return CDP_PATH_REGEX.test(pathname)
}

/**
 * Strips the origin from a URL href and returns a relative href (pathname + search + hash).
 */
export function getRelativeHref(url: URL) {
  return url.href.replace(url.origin, '')
}

/**
 * Isomorphic version of Next.js useSearchParams hook and NextRequest.nextUrl.searchParams.
 * @see https://nextjs.org/docs/app/api-reference/functions/use-search-params
 */
export async function isomorphicSearchParams(args?: { request?: NextRequest }) {
  const currentUrl = await getCurrentURL(args?.request)
  return new URLSearchParams(currentUrl.search)
}

// Shopify URL utilities

export interface BuildShopifyUrlOptions {
  productPath?: string
  includeEnglishLangSegment?: boolean
}

/**
 * Build a Shopify store URL for a given commerce-web locale.
 * Examples:
 *  - en-US → https://shop.bodi.com/products/shakeology
 *  - en-CA → https://shop-ca.bodi.com/products/shakeology
 *  - fr-CA → https://shop-ca.bodi.com/fr/products/shakeology
 *  - en-GB → https://shop-uk.bodi.com/products/shakeology
 */
export function buildShopifyProductUrl(
  localeCode: string,
  options: BuildShopifyUrlOptions = {}
): string {
  const { productPath = '', includeEnglishLangSegment = false } = options

  const locale = getLocaleFromCode(localeCode)
  const country = locale.countryCode as CountryCode
  const baseUrl = configPublic.shopify.baseUrlMap?.[country]
  if (!baseUrl) return ''

  // Get the language path segment from the locale's shopifyLanguagePathSegment property
  // For Shopify URLs, we typically omit the /en segment for English locales
  // Only include it for non-English languages (like /fr for fr-CA) or when explicitly requested
  const shopifyLangSegment = locale.shopifyLanguagePathSegment ?? ''
  const isEnglishSegment = shopifyLangSegment === '/en'

  // Use the language segment if it's not English, or use English segment if explicitly requested
  let langSegment = ''
  if (!isEnglishSegment) {
    langSegment = shopifyLangSegment
  } else if (includeEnglishLangSegment) {
    langSegment = '/en'
  }

  const cleanedPath = productPath.startsWith('/') ? productPath : `/${productPath}`
  return `${baseUrl}${langSegment}${cleanedPath}`
}

export function getShopifyBaseUrl(localeCode: string): string {
  const locale = getLocaleFromCode(localeCode)
  const country = locale.countryCode as CountryCode
  const baseUrl = configPublic.shopify.baseUrlMap?.[country] ?? ''
  const isFrenchLocale = localeCode === 'fr-CA' || localeCode === 'fr-FR'
  return isFrenchLocale ? `${baseUrl}/fr` : baseUrl
}

export function getShopifyAccountUrl(localeCode: string): string {
  const locale = getLocaleFromCode(localeCode)
  const country = locale.countryCode as CountryCode
  return configPublic.shopify.accountUrlMap?.[country] ?? ''
}

/** Returns '' when the country has no Shopify account URL. */
export function getShopifyAccountLogoutUrl(localeCode: string): string {
  const accountUrl = getShopifyAccountUrl(localeCode)
  if (!accountUrl) return ''

  try {
    const locale = getLocaleFromCode(localeCode)
    const logoutUrl = new URL('/logout', accountUrl)
    applyShopifyAccountLocale(logoutUrl, locale)

    const shopHome = getShopifyBaseUrl(localeCode)
    if (shopHome) {
      const returnUrl = new URL(shopHome)
      returnUrl.searchParams.set('country', locale.countryCode)
      logoutUrl.searchParams.set('return_url', returnUrl.href)
    }

    return logoutUrl.href
  } catch {
    log.warn('getShopifyAccountLogoutUrl received an unusable account URL', {
      localeCode,
      accountUrl,
    })
    return ''
  }
}

/**
 * Check if a URL hostname matches any of the Shopify base URLs
 */
export function isShopifyUrl(urlString: string | null | undefined): boolean {
  if (!urlString) return false

  try {
    const url = new URL(urlString)
    const shopifyBaseUrls = Object.values(configPublic.shopify.baseUrlMap || {})

    return shopifyBaseUrls.some((baseUrl) => {
      try {
        const shopifyUrl = new URL(baseUrl)
        return url.hostname === shopifyUrl.hostname
      } catch {
        return false
      }
    })
  } catch {
    return false
  }
}

/**
 * Check if a URL is a Shopify customer authentication login URL
 */
export function isShopifyLoginUrl(urlString: string | null | undefined): boolean {
  if (!urlString || !isShopifyUrl(urlString)) return false

  try {
    const url = new URL(urlString)
    return url.pathname === '/customer_authentication/login' && url.searchParams.has('return_to')
  } catch {
    return false
  }
}

/**
 * Wrap a Shopify URL with the Shopify customer authentication login URL
 * This is used for SSO flow to ensure users are logged into Shopify
 *
 * @param redirectUrl The original redirect URL (Shopify destination)
 * @returns The wrapped URL with Shopify login prepended, or original URL if not Shopify
 *
 * @example
 * Input: https://shop-us.qa.bodi.com/collections/all
 * Output: https://shop-us.qa.bodi.com/customer_authentication/login?return_to=%2Fcollections%2Fall
 *
 * @note Shopify's return_to parameter only works with relative URLs, not absolute URLs
 * @see https://shopify.dev/docs/storefronts/themes/login
 */
export function wrapWithShopifyLogin(
  redirectUrl: string | null | undefined
): string | null | undefined {
  if (!redirectUrl) {
    log.debug('[Shopify SSO] No redirectUrl provided')
    return redirectUrl
  }

  if (!isShopifyUrl(redirectUrl)) {
    log.debug('[Shopify SSO] URL is not a Shopify URL, skipping wrap', { redirectUrl })
    return redirectUrl
  }

  try {
    const url = new URL(redirectUrl)
    const shopifyLoginPath = '/customer_authentication/login'
    const shopifyLoginUrl = new URL(shopifyLoginPath, url.origin)

    // Shopify requires a relative URL for return_to parameter
    // Include pathname, search params, and hash
    const relativePath = `${url.pathname}${url.search}${url.hash}`
    shopifyLoginUrl.searchParams.set('return_to', relativePath)

    const wrappedUrl = shopifyLoginUrl.toString()
    log.info('[Shopify SSO] Wrapped Shopify URL with customer authentication login', {
      originalUrl: redirectUrl,
      wrappedUrl,
      relativePath,
    })

    return wrappedUrl
  } catch (error) {
    log.error('[Shopify SSO] Failed to wrap Shopify URL with login', { redirectUrl, error })
    return redirectUrl
  }
}

/**
 * Unwrap a Shopify customer authentication login URL to get the original destination
 * This is used when SSO fails and we want to redirect to the actual Shopify page without login
 *
 * @param redirectUrl The wrapped Shopify login URL
 * @returns The unwrapped destination URL, or original URL if not a Shopify login URL
 *
 * @example
 * Input: https://shop-us.qa.bodi.com/customer_authentication/login?return_to=%2Fcollections%2Fall
 * Output: https://shop-us.qa.bodi.com/collections/all
 */
export function unwrapShopifyLogin(
  redirectUrl: string | null | undefined
): string | null | undefined {
  if (!redirectUrl) {
    log.debug('[Shopify SSO] No redirectUrl to unwrap')
    return redirectUrl
  }

  if (!isShopifyLoginUrl(redirectUrl)) {
    log.debug('[Shopify SSO] URL is not a Shopify login URL, skipping unwrap', { redirectUrl })
    return redirectUrl
  }

  try {
    const url = new URL(redirectUrl)
    const returnTo = url.searchParams.get('return_to')

    if (!returnTo) {
      log.warn('[Shopify SSO] Shopify login URL missing return_to parameter', { redirectUrl })
      return redirectUrl
    }

    // Construct the full destination URL from the relative path
    const unwrappedUrl = new URL(returnTo, url.origin).toString()
    log.info('[Shopify SSO] Unwrapped Shopify login URL to destination', {
      wrappedUrl: redirectUrl,
      unwrappedUrl,
      returnTo,
    })

    return unwrappedUrl
  } catch (error) {
    log.error('[Shopify SSO] Failed to unwrap Shopify login URL', { redirectUrl, error })
    return redirectUrl
  }
}

/**
 * Normalize a URL by moving query parameters from the hash to the main URL.
 * This is necessary due to Social Ladder appending query params after the hash
 * @param urlString The URL string to normalize.
 * @returns An object containing the normalized URL and a flag indicating if a redirect is needed.
 */
export function normalizeURL(urlString: string): { url: URL; urlChanged: boolean } {
  if (!URL.canParse(urlString)) {
    throw new Error('Invalid URL')
  }

  const url = new URL(urlString)

  // Check if there are query parameters after the hash
  if (url.hash.includes('?')) {
    const [hash, query] = url.hash.split('?')
    if (!query) {
      url.hash = `${hash}?`
    } else {
      url.search = url.search ? `${url.search}&${query}` : `?${query}`
      url.hash = hash
    }
  }

  return { url, urlChanged: url.toString() !== urlString }
}

/**
 *
 * @param type ContentType or EntityType
 * @returns Path prefix for Cocoa API content routes
 *
 */
export const getRoutePrefixByContentType = (type?: CocoApi.ContentType | CocoApi.EntityType) => {
  switch (type) {
    case 'bodiBlock':
      return 'blocks'
    case 'program':
      return 'programs'
    case 'compilation':
      return 'content'
    default:
      return 'programs'
  }
}
